import { RegisterableHotkey, formatForDisplay } from '@tanstack/react-hotkeys';
import { View } from '@shared/components/View';
import classNames from './KeyCaps.module.css';

interface KeyCapsProps {
  hotkey: RegisterableHotkey;
  size?: number;
  className?: string;
}

/**
 * `formatForDisplay` picks its own separator by platform — a space on macOS with
 * symbols, `+` elsewhere — so splitting its output back apart means guessing
 * which one it used. Asking for a separator instead makes that deterministic,
 * and a NUL can't collide with anything: with `useSymbols: false` some labels
 * contain spaces ("Left Bracket"), which a space separator would split into two
 * keycaps.
 */
const SEPARATOR = '\u0000';

export function KeyCaps({ hotkey, size, className }: KeyCapsProps) {
  // Empty segments are dropped because a hotkey bound to `+` arrives already
  // broken: `formatForDisplay` splits the canonical form on `+` internally, so
  // `Mod++` formats as `⌘` plus an empty segment no matter what separator we
  // ask for. Showing the modifiers alone beats rendering a blank keycap.
  const keys = formatForDisplay(hotkey, { separatorToken: SEPARATOR })
    .split(SEPARATOR)
    .filter(Boolean);

  return (
    <View className={[classNames.keycaps, className]} vars={{ 'key-font-size': size }}>
      {keys.map((key, index) => (
        // Positional key: `keys` is derived from `hotkey`, and a chord can repeat
        // a glyph, so the segment alone isn't guaranteed unique.
        <View key={index} className={classNames.keycap}>
          {key}
        </View>
      ))}
    </View>
  );
}
